import { useMutation } from '@apollo/client/react'
import AcceptInvite from '../graphql/queries/AcceptInvite'
import { useEffect, useState, useCallback } from 'react'
import { Modal, Stack, Text, Loader } from '@mantine/core'
import { useModalStack } from '../ui/ModalStack'
import { useAppContext, useAppDispatch } from '../AppContext'
import { Actions } from '../AppReducer'

export default function AcceptingInviteModal({ opened, onClose, inviteId, email, ...props }) {
    const { pushModal, clearModals } = useModalStack()
    const appState = useAppContext()
    const dispatch = useAppDispatch()
    const isLoggedIn = appState?.loggedIn
    const [acceptInviteMutation] = useMutation(AcceptInvite)
    const [acceptStarted, setAcceptStarted] = useState(false)

    const startAcceptInvite = useCallback(async () => {
        try {
            const result = await acceptInviteMutation({ variables: { id: inviteId } })
            const response = result?.data?.acceptInvite

            if (response) {
                // Extract room ID from the invite
                const roomId = response.invite?.room?.id
                const roomName = response.invite?.room?.name

                // Close this modal
                onClose?.()

                if (response.passwordResetToken) {
                    // New user was created, they need to set a password
                    pushModal('resetPassword', {
                        resetToken: response.passwordResetToken,
                        email: email,
                        isNewAccount: true,
                        roomId: roomId,
                        roomName: roomName
                    })
                } else if (response.user) {
                    // User was already logged in and invite was accepted
                    // Navigate to the room if we have the room ID
                    if (roomId) {
                        dispatch(Actions.setCurrentRoom(roomId))
                    }
                    pushModal('alert', {
                        title: 'Invitation Accepted',
                        text: `You have successfully joined ${roomName || 'the room'}!`,
                        okText: 'OK'
                    })
                } else {
                    // Unexpected response
                    pushModal('alert', {
                        title: 'Success',
                        text: 'Invitation accepted! Please log in to continue.',
                        okText: 'Log In',
                        onClose: () => {
                            clearModals()
                            pushModal('emailLogin', { roomId })
                        }
                    })
                }
            } else {
                // Failed to accept invite
                onClose?.()
                pushModal('alert', {
                    title: 'Invitation Failed',
                    text: 'This invitation is no longer valid or has already been used.',
                    okText: 'OK'
                })
            }
        } catch (error) {
            console.error('Accept invite error:', error)
            onClose?.()

            // Show appropriate error based on login state
            if (isLoggedIn) {
                pushModal('alert', {
                    title: 'Invitation Failed',
                    text: error.message || 'Unable to accept the invitation. It may have expired or already been used.',
                    okText: 'OK'
                })
            } else {
                pushModal('alert', {
                    title: 'Invitation Failed',
                    text: error.message || 'Unable to accept the invitation. It may have expired or already been used. Please try logging in.',
                    okText: 'Log In',
                    onClose: () => {
                        clearModals()
                        pushModal('emailLogin')
                    }
                })
            }
        }
    }, [inviteId, email, acceptInviteMutation, onClose, pushModal, isLoggedIn, clearModals, dispatch])

    useEffect(() => {
        if (opened && !acceptStarted && inviteId) {
            setAcceptStarted(true)
            startAcceptInvite()
        }
    }, [opened, acceptStarted, inviteId, startAcceptInvite])

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title="Accepting Invitation"
            centered
            closeOnClickOutside={false}
            closeOnEscape={false}
            withCloseButton={false}
            {...props}
        >
            <Stack gap="md" align="center" py="md">
                <Text size="sm">Accepting your invitation...</Text>
                <Loader size="md" />
            </Stack>
        </Modal>
    )
}

